import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Select,
  // DatePicker,
  Input,
  Modal,
  Row,
  Col,
  message,
  Typography,
  Descriptions,
  Alert,
  Statistic,
  Progress,
  Badge,
  // Steps,
  Tooltip,
  Popconfirm,
  Menu,
  Checkbox,
  Dropdown,
  Tabs
} from 'antd';
import {
  InboxOutlined,
  ScanOutlined,
  CheckCircleOutlined,
  TruckOutlined,
  PrinterOutlined,
  BarcodeOutlined,
  EnvironmentOutlined,
  UserOutlined,
  PhoneOutlined,
  FileTextOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  SettingOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import useDraggableColumns from '../../../../components/table/useDraggableColumns';
import moment from 'moment';
import type { 
  PackingOrder,
  PackingFilters,
  PackingStats
} from '../../types/packing';
import PackingScanInterface from '../PackingScanInterface';
import PickupScanInterface from '../PickupScanInterface';
import apiService from '../../../../services/apiService';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore.ts';
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;
const { Option } = Select;

interface PackingManagementProps {
  onClose?: () => void;
}

const PackingManagement: React.FC<PackingManagementProps> = ({ onClose: _onClose }) => {
    const { t } = useTranslation();
  const { user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'packing' | 'pickup' | 'delivery' | 'scanning' | 'pickup-scanning'>(user?.role === 'PICKUP_SCANNING' ? 'pickup' : 'scanning');
  const [selectedOrderForScan, setSelectedOrderForScan] = useState<PackingOrder | null>(null);
  const [packingOrders, setPackingOrders] = useState<PackingOrder[]>([]);
  // const [deliveryNotes, setDeliveryNotes] = useState<DeliveryNote[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<PackingOrder | null>(null);
  // 分頁獨立的查詢條件：打包/攬收各自維護
  const [packingFilters, setPackingFilters] = useState<PackingFilters>({});
  const [pickupFilters, setPickupFilters] = useState<PackingFilters>({});
  const [loading, setLoading] = useState(false);

  // Debounced search input values (separate from actual filter values)
  const [packingSearchInput, setPackingSearchInput] = useState('');
  const [pickupSearchInput, setPickupSearchInput] = useState('');
  // 分頁狀態（與其他頁面一致）
  const [packingPage, setPackingPage] = useState(1);
  const [packingPageSize, setPackingPageSize] = useState(10);
  const [pickupPage, setPickupPage] = useState(1);
  const [pickupPageSize, setPickupPageSize] = useState(10);
  const [packingTotal, setPackingTotal] = useState(0);
  const [pickupTotal, setPickupTotal] = useState(0);
  const [splitLoading, setSplitLoading] = useState(false);
  
  // KPI 狀態
  const [kpi, setKpi] = useState<{ verifying: number; packed: number; readyForPickup: number }>({ verifying: 0, packed: 0, readyForPickup: 0 });
  
  // 攬收 KPI 狀態
  const [pickupKpi, setPickupKpi] = useState<{ packed: number; readyForPickup: number }>({ packed: 0, readyForPickup: 0 });
  
  // 包裝訂單表格排序狀態
  const [packingSortField, setPackingSortField] = useState<string>('');
  const [packingSortOrder, setPackingSortOrder] = useState<'asc' | 'desc' | ''>('');

  

  // 包裝訂單表格列配置狀態
  const [packingColumnSettings, setPackingColumnSettings] = useState(() => {
    const saved = localStorage.getItem('packing-columns-config');
    const version = localStorage.getItem('packing-columns-version');
    
    if (version !== '2025-01-v1' || !saved) {
      const defaultConfig = {
        orderNumber: { visible: true, order: 0 },
        waybillNumber: { visible: true, order: 1 },
        customerName: { visible: true, order: 2 },
        receiverName: { visible: true, order: 3 },
        status: { visible: true, order: 4 },
        packingZoneName: { visible: true, order: 5 },
        pickupZoneName: { visible: true, order: 6 },
        totalItems: { visible: true, order: 7 },
        totalQuantity: { visible: true, order: 8 },
        packageWeight: { visible: true, order: 9 },
        progress: { visible: true, order: 10 },
        deliveryCompany: { visible: true, order: 11 },
        trackingNumber: { visible: true, order: 12 },
        discrepancies: { visible: false, order: 13 },
        actions: { visible: true, order: 14 }
      } as any;
      localStorage.setItem('packing-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('packing-columns-version', '2025-01-v1');
      return defaultConfig;
    }
    
    try {
      return JSON.parse(saved);
    } catch {
      const defaultConfig = {
        orderNumber: { visible: true, order: 0 },
        waybillNumber: { visible: true, order: 1 },
        customerName: { visible: true, order: 2 },
        receiverName: { visible: true, order: 3 },
        status: { visible: true, order: 4 },
        packingZoneName: { visible: true, order: 5 },
        pickupZoneName: { visible: true, order: 6 },
        totalItems: { visible: true, order: 7 },
        totalQuantity: { visible: true, order: 8 },
        packageWeight: { visible: true, order: 9 },
        progress: { visible: true, order: 10 },
        deliveryCompany: { visible: true, order: 11 },
        trackingNumber: { visible: true, order: 12 },
        discrepancies: { visible: false, order: 13 },
        actions: { visible: true, order: 14 }
      } as any;
      return defaultConfig;
    }
  });

  // KPI刷新函数
  const refreshKpi = async () => {
    try {
      const resp: any = await apiService.getPackingKPI();
      const d = resp?.data || resp;
      if (d) setKpi({
        verifying: Number(d.verifying || 0),
        packed: Number(d.packed || 0),
        readyForPickup: Number(d.ready_for_pickup || 0)
      });
    } catch {}
  };

  // 載入 KPI
  useEffect(() => {
    // 初始加载
    refreshKpi();

    // 每30秒自动刷新一次KPI数据
    const intervalId = setInterval(refreshKpi, 30000);

    // 组件卸载时清除定时器
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // 处理从picking跳转过来的自动打开扫描页面
  useEffect(() => {
    const state = location.state as any;
    if (state?.autoOpenScanning && state?.packingId) {
      (async () => {
        try {
          // 加载packing订单详情
          const detail: any = await apiService.getPackingOrderDetail(state.packingId);
          if (detail) {
            const packingOrder = {
              ...detail,
              createdAt: detail.createdAt ? new Date(detail.createdAt) : undefined,
              updatedAt: detail.updatedAt ? new Date(detail.updatedAt) : undefined,
              packedAt: detail.packedAt ? new Date(detail.packedAt) : undefined,
              verifiedAt: detail.verifiedAt ? new Date(detail.verifiedAt) : undefined,
              pickedUpAt: detail.pickedUpAt ? new Date(detail.pickedUpAt) : undefined,
              items: detail.items || []
            };
            setSelectedOrderForScan(packingOrder as any);
            setActiveTab('scanning');
            
            // 清除location state，避免重复触发
            navigate(location.pathname, { replace: true, state: {} });
          }
        } catch (error) {
          console.error('加載包裝訂單失敗:', error);
          message.error(t('無法加載包裝訂單，請手動選擇'));
        }
      })();
    }
  }, [location.state]);

  // 攬收管理表格排序狀態
  const [pickupSortField, setPickupSortField] = useState<string>('');
  const [pickupSortOrder, setPickupSortOrder] = useState<'asc' | 'desc' | ''>('');

  // 攬收管理表格列配置狀態
  const [pickupColumnSettings, setPickupColumnSettings] = useState(() => {
    const saved = localStorage.getItem('pickup-columns-config');
    const version = localStorage.getItem('pickup-columns-version');
    
    if (version !== '2025-02-v2' || !saved) {
      const defaultConfig = {
        orderNumber: { visible: true, order: 0 },
        waybillNumber: { visible: true, order: 1 },
        customerName: { visible: true, order: 2 },
        receiverName: { visible: true, order: 3 },
        receiverContact: { visible: true, order: 4 },
        pickupZoneName: { visible: true, order: 5 },
        packageType: { visible: true, order: 6 },
        packageWeight: { visible: true, order: 7 },
        packageDimensions: { visible: true, order: 8 },
        packageValue: { visible: true, order: 9 },
        deliveryCompany: { visible: true, order: 10 },
        trackingNumber: { visible: true, order: 11 },
        packedAt: { visible: false, order: 12 },
        status: { visible: true, order: 13 },
        actions: { visible: true, order: 14 }
      };
      localStorage.setItem('pickup-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('pickup-columns-version', '2025-02-v2');
      return defaultConfig;
    }
    
    try {
      return JSON.parse(saved);
    } catch {
      const defaultConfig = {
        orderNumber: { visible: true, order: 0 },
        waybillNumber: { visible: true, order: 1 },
        customerName: { visible: true, order: 2 },
        receiverName: { visible: true, order: 3 },
        receiverContact: { visible: true, order: 4 },
        pickupZoneName: { visible: true, order: 5 },
        packageType: { visible: true, order: 6 },
        packageWeight: { visible: true, order: 7 },
        packageDimensions: { visible: true, order: 8 },
        packageValue: { visible: true, order: 9 },
        deliveryCompany: { visible: true, order: 10 },
        trackingNumber: { visible: true, order: 11 },
        packedAt: { visible: false, order: 12 },
        status: { visible: true, order: 13 },
        actions: { visible: true, order: 14 }
      };
      return defaultConfig;
    }
  });

  // 配送單管理表格排序狀態
  const [deliverySortField, setDeliverySortField] = useState<string>('');
  const [deliverySortOrder, setDeliverySortOrder] = useState<'asc' | 'desc' | ''>('');

  // 配送單管理表格列配置狀態
  const [deliveryColumnSettings, setDeliveryColumnSettings] = useState(() => {
    const saved = localStorage.getItem('delivery-columns-config');
    const version = localStorage.getItem('delivery-columns-version');
    
    if (version !== '2024-01-v2' || !saved) {
      const defaultConfig = {
        deliveryNumber: { visible: true, order: 0 },
        customer: { visible: true, order: 1 },
        deliveryAddress: { visible: true, order: 2 },
        delivery: { visible: true, order: 3 },
        packageInfo: { visible: true, order: 4 },
        status: { visible: true, order: 5 },
        createdAt: { visible: false, order: 6 },
        actions: { visible: true, order: 7 }
      };
      localStorage.setItem('delivery-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('delivery-columns-version', '2024-01-v2');
      return defaultConfig;
    }
    
    try {
      return JSON.parse(saved);
    } catch {
      const defaultConfig = {
        deliveryNumber: { visible: true, order: 0 },
        customer: { visible: true, order: 1 },
        deliveryAddress: { visible: true, order: 2 },
        delivery: { visible: true, order: 3 },
        packageInfo: { visible: true, order: 4 },
        status: { visible: true, order: 5 },
        createdAt: { visible: false, order: 6 },
        actions: { visible: true, order: 7 }
      };
      return defaultConfig;
    }
  });
  
  // Modal states
  const [packingDetailVisible, setPackingDetailVisible] = useState(false);
  // const [scanningVisible, setScanningVisible] = useState(false);
  const [deliveryNoteVisible, setDeliveryNoteVisible] = useState(false);
  // 列設置下拉菜單可見狀態
  const [packingColumnSettingsVisible, setPackingColumnSettingsVisible] = useState(false);
  const [pickupColumnSettingsVisible, setPickupColumnSettingsVisible] = useState(false);
  const [deliveryColumnSettingsVisible, setDeliveryColumnSettingsVisible] = useState(false);

  // 處理包裝訂單列排序
  const handlePackingColumnSort = (columnKey: string) => {
    if (packingSortField === columnKey) {
      setPackingSortOrder(packingSortOrder === 'asc' ? 'desc' : packingSortOrder === 'desc' ? '' : 'asc');
      if (packingSortOrder === 'desc') {
        setPackingSortField('');
      }
    } else {
      setPackingSortField(columnKey);
      setPackingSortOrder('asc');
    }
  };

  // 獲取包裝訂單排序圖標
  const getPackingSortIcon = (columnKey: string) => {
    if (packingSortField !== columnKey) return null;
    return packingSortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  // 處理包裝訂單列可見性切換
  const handlePackingColumnVisibilityChange = (columnKey: string, visible: boolean) => {
    const newSettings = {
      ...packingColumnSettings,
      [columnKey]: { ...packingColumnSettings[columnKey], visible }
    };
    setPackingColumnSettings(newSettings);
    localStorage.setItem('packing-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('packing-columns-version', '2024-01-v2');
  };

  // 處理攬收管理列排序
  const handlePickupColumnSort = (columnKey: string) => {
    if (pickupSortField === columnKey) {
      setPickupSortOrder(pickupSortOrder === 'asc' ? 'desc' : pickupSortOrder === 'desc' ? '' : 'asc');
      if (pickupSortOrder === 'desc') {
        setPickupSortField('');
      }
    } else {
      setPickupSortField(columnKey);
      setPickupSortOrder('asc');
    }
  };

  // 獲取攬收管理排序圖標
  const getPickupSortIcon = (columnKey: string) => {
    if (pickupSortField !== columnKey) return null;
    return pickupSortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  // 處理攬收管理列可見性切換
  const handlePickupColumnVisibilityChange = (columnKey: string, visible: boolean) => {
    const newSettings = {
      ...pickupColumnSettings,
      [columnKey]: { ...pickupColumnSettings[columnKey], visible }
    };
    setPickupColumnSettings(newSettings);
    localStorage.setItem('pickup-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('pickup-columns-version', '2024-01-v2');
  };

  // 處理配送單管理列排序
  const handleDeliveryColumnSort = (columnKey: string) => {
    if (deliverySortField === columnKey) {
      setDeliverySortOrder(deliverySortOrder === 'asc' ? 'desc' : deliverySortOrder === 'desc' ? '' : 'asc');
      if (deliverySortOrder === 'desc') {
        setDeliverySortField('');
      }
    } else {
      setDeliverySortField(columnKey);
      setDeliverySortOrder('asc');
    }
  };

  // 獲取配送單管理排序圖標
  const getDeliverySortIcon = (columnKey: string) => {
    if (deliverySortField !== columnKey) return null;
    return deliverySortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  // 處理配送單管理列可見性切換
  const handleDeliveryColumnVisibilityChange = (columnKey: string, visible: boolean) => {
    const newSettings: any = {
      ...deliveryColumnSettings,
      [columnKey]: { ...(deliveryColumnSettings as any)[columnKey], visible }
    } as any;
    setDeliveryColumnSettings(newSettings);
    localStorage.setItem('delivery-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('delivery-columns-version', '2024-01-v2');
  };

  // 已移除 mock 資料，改由後端 API 取得

  const mockStats: PackingStats = {
    totalOrders: 32,
    pendingPacking: 1,
    inPacking: 1,
    readyForPickup: 1,
    shipped: 1,
    delivered: 1,
    averagePackingTime: 45, // minutes
    packingEfficiency: 92, // percentage
    discrepancyRate: 3.2 // percentage
  };

  // Debounce search input for packing tab (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setPackingFilters(prev => ({ ...prev, searchKeyword: packingSearchInput }));
    }, 300);
    return () => clearTimeout(timer);
  }, [packingSearchInput]);

  // Debounce search input for pickup tab (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setPickupFilters(prev => ({ ...prev, searchKeyword: pickupSearchInput }));
    }, 300);
    return () => clearTimeout(timer);
  }, [pickupSearchInput]);

  // 針對各自分頁獨立載入資料
  useEffect(() => {
    if (activeTab === 'packing') {
      loadPackingData();
    }
  }, [activeTab, packingPage, packingPageSize, packingFilters.searchKeyword, packingFilters.status]);

  useEffect(() => {
    if (activeTab === 'pickup' || activeTab === 'pickup-scanning') {
      loadPickupData();
    }
  }, [activeTab, pickupPage, pickupPageSize, pickupFilters.searchKeyword, pickupFilters.status]);

  const loadPackingData = async () => {
    setLoading(true);
    try {
      const params: any = { page: packingPage, limit: packingPageSize };
      if (packingFilters.searchKeyword) params.search = packingFilters.searchKeyword;
      if (packingFilters.status && packingFilters.status.length > 0) params.status = packingFilters.status[0];
      const resp: any = await apiService.getPackingOrders(params);
      const rows: any[] = resp.data || [];
      const mapped = rows.map((r: any) => ({
        ...r,
        createdAt: r.createdAt ? new Date(r.createdAt) : undefined,
        updatedAt: r.updatedAt ? new Date(r.updatedAt) : undefined,
        packedAt: r.packedAt ? new Date(r.packedAt) : undefined,
        verifiedAt: r.verifiedAt ? new Date(r.verifiedAt) : undefined,
        pickedUpAt: r.pickedUpAt ? new Date(r.pickedUpAt) : undefined,
        totalItems: r.totalItems ?? r.total_items ?? (Array.isArray(r.items) ? r.items.length : 0),
        totalQuantity: r.totalQuantity ?? r.total_quantity ?? 0,
        items: r.items || []
      }));
      setPackingOrders(mapped as any);
      const totalCandidates = [
        (resp as any)?.pagination?.total,
        (resp as any)?.total,
        (resp as any)?.totalCount,
        (resp as any)?.count
      ];
      const total = totalCandidates.find((v) => typeof v === 'number' && v >= 0);
      setPackingTotal((total as number | undefined) ?? mapped.length);
    } catch (e: any) {
      console.error('load packing data error:', e);
      message.error(e?.message || t('加載包裝/攬收列表失敗'));
      setPackingOrders([]);
      setPackingTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // 單獨加載攬收 KPI 數據的函數
  const loadPickupKpi = async () => {
    try {
      console.log('[PackingManagement] Loading pickup KPI data...');
      
      // 獲取已包裝訂單數量
      const packedResp: any = await apiService.getPackingOrders({ status: 'packed', limit: 1 });
      console.log('[PackingManagement] Packed response:', packedResp);
      const packedCount = packedResp?.pagination?.total || packedResp?.total || packedResp?.count || 0;
      
      // 獲取待攬收訂單數量
      const readyResp: any = await apiService.getPackingOrders({ status: 'ready_for_pickup', limit: 1 });
      console.log('[PackingManagement] Ready for pickup response:', readyResp);
      const readyCount = readyResp?.pagination?.total || readyResp?.total || readyResp?.count || 0;
      
      console.log('[PackingManagement] KPI counts - packed:', packedCount, 'ready:', readyCount);
      
      setPickupKpi({
        packed: Number(packedCount) || 0,
        readyForPickup: Number(readyCount) || 0
      });
    } catch (kpiErr) {
      console.error('[PackingManagement] Load pickup KPI error:', kpiErr);
      // 設置為 0 避免顯示錯誤
      setPickupKpi({
        packed: 0,
        readyForPickup: 0
      });
    }
  };

  const loadPickupData = async () => {
    setLoading(true);
    try {
      const params: any = { page: pickupPage, limit: pickupPageSize };
      if (pickupFilters.searchKeyword) params.search = pickupFilters.searchKeyword;
      if (pickupFilters.status && pickupFilters.status.length > 0) params.status = pickupFilters.status[0];
      const resp: any = await apiService.getPackingPickups(params);
      const rows: any[] = resp.data || [];
      const mapped = rows.map((r: any) => ({
        ...r,
        createdAt: r.createdAt ? new Date(r.createdAt) : undefined,
        updatedAt: r.updatedAt ? new Date(r.updatedAt) : undefined,
        packedAt: r.packedAt ? new Date(r.packedAt) : undefined,
        verifiedAt: r.verifiedAt ? new Date(r.verifiedAt) : undefined,
        pickedUpAt: r.pickedUpAt ? new Date(r.pickedUpAt) : undefined,
        totalItems: r.totalItems ?? r.total_items ?? (Array.isArray(r.items) ? r.items.length : 0),
        totalQuantity: r.totalQuantity ?? r.total_quantity ?? 0,
        items: r.items || []
      }));
      setPackingOrders(mapped as any);
      const totalCandidates = [
        (resp as any)?.pagination?.total,
        (resp as any)?.total,
        (resp as any)?.totalCount,
        (resp as any)?.count
      ];
      const total = totalCandidates.find((v) => typeof v === 'number' && v >= 0);
      setPickupTotal((total as number | undefined) ?? mapped.length);
      
      // 加載攬收 KPI 數據
      await loadPickupKpi();
    } catch (e: any) {
      console.error('load pickup data error:', e);
      message.error(e?.message || t('加載攬收列表失敗'));
      setPackingOrders([]);
      setPickupTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // 導出功能 - 客戶端CSV生成
  const handleExportExcel = (type: 'packing' | 'pickup' | 'delivery') => {
    try {
      let csvContent = '';
      let filename = '';
      
      switch (type) {
        case 'packing':
          csvContent = generatePackingOrdersCSVContent(filteredPackingOrders);
          filename = `packing_orders_export_${new Date().toISOString().split('T')[0]}.csv`;
          break;
        case 'pickup':
          csvContent = generatePickupOrdersCSVContent(pickupOrders);
          filename = `pickup_orders_export_${new Date().toISOString().split('T')[0]}.csv`;
          break;
        case 'delivery':
          csvContent = generateDeliveryOrdersCSVContent([]);
          filename = `delivery_orders_export_${new Date().toISOString().split('T')[0]}.csv`;
          break;
      }
      
      if (csvContent) {
        // 創建Blob並下載
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        message.success(t('包裝管理數據已成功導出'));
      }
    } catch (error) {
      console.error('導出失敗:', error);
      message.error(t('導出失敗，請稍後重試'));
    }
  };

  // 生成包裝訂單CSV內容
  const generatePackingOrdersCSVContent = (data: PackingOrder[]) => {
    const headers = [
      t('訂單ID'), t('出庫訂單ID'), t('訂單號'), t('運單ID'), t('運單號'), t('狀態'), t('包裝區域'), t('攬收區域'),
      t('包裝人員'), t('包裝時間'), t('驗證人員'), t('驗證時間'), t('包裝類型'), t('包裝重量'), t('包裝尺寸'),
      t('包裝價值'), t('客戶名稱'), t('客戶聯繫方式'), t('配送地址'), t('收件人'), t('收件人聯繫方式'),
      t('追蹤號'), t('配送公司'), t('創建時間'), t('更新時間')
    ];

    const rows = data.map(item => [
      item.id || '',
      item.outboundOrderId || '',
      item.orderNumber || '',
      item.waybillId || '',
      item.waybillNumber || '',
      item.status || '',
      item.packingZoneName || '',
      item.pickupZoneName || '',
      item.packedBy || '',
      item.packedAt ? `\u200B${new Date(item.packedAt).toISOString().split('T')[0]}` : '',
      item.verifiedBy || '',
      item.verifiedAt ? `\u200B${new Date(item.verifiedAt).toISOString().split('T')[0]}` : '',
      item.packageType || '',
      item.packageWeight || '',
      item.packageDimensions || '',
      item.packageValue || '',
      item.customerName || '',
      item.customerContact || '',
      item.deliveryAddress || '',
      item.receiverName || '',
      item.receiverContact || '',
      item.trackingNumber || '',
      item.deliveryCompany || '',
      item.createdAt ? `\u200B${new Date(item.createdAt).toISOString().split('T')[0]}` : '',
      item.updatedAt ? `\u200B${new Date(item.updatedAt).toISOString().split('T')[0]}` : ''
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  // 生成攬收訂單CSV內容
  const generatePickupOrdersCSVContent = (data: PackingOrder[]) => {
    const headers = [
      t('訂單號'), t('狀態'), t('客戶名稱'), t('收件人'), t('配送地址'), t('攬收人員'), t('攬收時間'),
      t('配送公司'), t('追蹤號'), t('包裝重量'), t('包裝尺寸'), t('創建時間')
    ];

    const rows = data.map(item => [
      item.orderNumber || '',
      item.status || '',
      item.customerName || '',
      item.receiverName || '',
      item.deliveryAddress || '',
      item.pickedUpBy || '',
      item.pickedUpAt ? `\u200B${new Date(item.pickedUpAt).toISOString().split('T')[0]}` : '',
      item.deliveryCompany || '',
      item.trackingNumber || '',
      item.packageWeight || '',
      item.packageDimensions || '',
      item.createdAt ? `\u200B${new Date(item.createdAt).toISOString().split('T')[0]}` : ''
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  // 生成配送訂單CSV內容
  const generateDeliveryOrdersCSVContent = (data: any[]) => {
    const headers = [t('配送單號'), t('狀態'), t('客戶名稱'), t('配送地址'), t('配送時間')];
    const rows = data.map(item => [item.id || '', item.status || '', item.customer || '', item.address || '', item.date || '']);
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    setLoading(true);
    try {
      // 後端狀態更新
      if (newStatus === 'shipped') {
        await apiService.confirmPackingPickup(orderId, {});
      }
      
      const updatedOrders = packingOrders.map(order => {
        if (order.id === orderId) {
          const updatedOrder = { ...order, status: newStatus as any, updatedAt: new Date() };
          
          // Update location based on status
          if (newStatus === 'ready_for_pickup') {
            // Move to pickup zone
            updatedOrder.items = order.items.map(item => ({
              ...item,
              currentLocation: order.pickupZoneId,
              targetLocation: order.pickupZoneId
            }));
          }
          
          return updatedOrder;
        }
        return order;
      });
      
      setPackingOrders(updatedOrders);
      message.success(t('訂單狀態已更新為 {{status}}', { status: getStatusText(newStatus) }));
    } finally {
      setLoading(false);
    }
  };

  const handlePrintDeliveryNote = async (packingOrder: PackingOrder) => {
    try {
      await apiService.downloadLocalWaybillPDF(packingOrder.waybillNumber, packingOrder.orderNumber);
      message.success(t('配送單PDF已開始下載'));
    } catch (error) {
      console.error('下載配送單PDF失敗:', error);
      message.error(t('下載配送單PDF失敗，請稍後重試'));
    }
  };

  /* const generateDeliveryNotePrintHTML = (packingOrder: PackingOrder): string => {
    const deliveryOrderNumber = packingOrder.deliveryNoteNumber || `DO-${moment().format('YYYYMMDD')}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;
    
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>配送單 - ${deliveryOrderNumber}</title>
  <style>
    body { font-family: "Microsoft JhengHei", Arial, sans-serif; line-height: 1.4; margin: 0; padding: 15px; color: #333; font-size: 12px; }
    .delivery-note { max-width: 400px; margin: 0 auto; border: 2px solid #000; padding: 10px; }
    .header { text-align: center; border-bottom: 1px solid #000; padding-bottom: 8px; margin-bottom: 10px; }
    .company-title { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
    .document-title { font-size: 16px; font-weight: bold; margin-bottom: 3px; }
    .delivery-number { font-size: 14px; margin-bottom: 3px; }
    .section { margin-bottom: 8px; border: 1px solid #ccc; padding: 4px; }
    .section-title { font-weight: bold; font-size: 11px; background: #f5f5f5; padding: 2px 4px; margin: -4px -4px 4px -4px; border-bottom: 1px solid #ccc; }
    .info-row { display: flex; justify-content: space-between; margin-bottom: 2px; font-size: 11px; }
    .info-label { font-weight: bold; width: 50px; }
    .info-value { flex: 1; }
    .address-box { border: 1px solid #000; padding: 4px; margin: 2px 0; min-height: 30px; font-size: 10px; }
    .items-table { width: 100%; border-collapse: collapse; margin: 4px 0; font-size: 10px; }
    .items-table th, .items-table td { border: 1px solid #ccc; padding: 3px 2px; text-align: left; }
    .items-table th { background: #f5f5f5; font-weight: bold; }
    .signature-section { display: flex; justify-content: space-between; margin-top: 8px; }
    .signature-box { border: 1px solid #ccc; width: 48%; height: 40px; position: relative; }
    .signature-label { position: absolute; top: -6px; left: 4px; background: white; padding: 0 2px; font-size: 9px; }
    .footer { border-top: 1px solid #000; padding-top: 4px; margin-top: 8px; font-size: 9px; text-align: center; }
    @media print { body { margin: 0; padding: 10px; } .delivery-note { max-width: none; } }
  </style>
</head>
<body>
  <div class="delivery-note">
    <div class="header">
      <div class="company-title">Your Company Name</div>
      <div class="document-title">配送單 / DELIVERY NOTE</div>
      <div class="delivery-number">單號: ${deliveryOrderNumber}</div>
      <div style="font-size: 12px;">日期: ${moment().format('YYYY-MM-DD HH:mm')}</div>
    </div>

    <div class="section">
      <div class="section-title">配送信息 DELIVERY INFO</div>
      <div class="info-row">
        <span class="info-label">客戶:</span>
        <span class="info-value">${packingOrder.customerName}</span>
      </div>
      <div class="info-row">
        <span class="info-label">收件人:</span>
        <span class="info-value">${packingOrder.receiverName}</span>
      </div>
      <div class="info-row">
        <span class="info-label">聯絡:</span>
        <span class="info-value">${packingOrder.receiverContact}</span>
      </div>
      <div class="info-row">
        <span class="info-label">配送日:</span>
        <span class="info-value">${moment().format('YYYY-MM-DD')}</span>
      </div>
      <div class="info-row">
        <span class="info-label">配送時:</span>
        <span class="info-value">09:00-18:00</span>
      </div>
      <div class="address-box">
        <strong>配送地址:</strong><br>
        ${packingOrder.deliveryAddress}
      </div>
    </div>

    <div class="section">
      <div class="section-title">包裹信息 PACKAGE INFO</div>
      <div class="info-row">
        <span class="info-label">配貨單:</span>
        <span class="info-value">${packingOrder.waybillNumber}</span>
      </div>
      <div class="info-row">
        <span class="info-label">追蹤號:</span>
        <span class="info-value">${packingOrder.trackingNumber || '-'}</span>
      </div>
      <div class="info-row">
        <span class="info-label">重量:</span>
        <span class="info-value">${packingOrder.packageWeight}kg</span>
      </div>
      <div class="info-row">
        <span class="info-label">尺寸:</span>
        <span class="info-value">${packingOrder.packageDimensions}</span>
      </div>
      <div class="info-row">
        <span class="info-label">件數:</span>
        <span class="info-value">${packingOrder.totalItems}件</span>
      </div>
      <div class="info-row">
        <span class="info-label">數量:</span>
        <span class="info-value">${packingOrder.totalQuantity}個</span>
      </div>
    </div>

    <div class="section">
      <div class="section-title">商品明細 ITEM DETAILS</div>
      <table class="items-table">
        <thead>
          <tr>
            <th style="width: 30%;">商品名稱</th>
            <th style="width: 20%;">SKU</th>
            <th style="width: 15%;">數量</th>
            <th style="width: 35%;">備註</th>
          </tr>
        </thead>
        <tbody>
          ${packingOrder.items.map(item => `
            <tr>
              <td>${item.productName}</td>
              <td>${item.productSku}</td>
              <td style="text-align: center;">${item.packedQuantity}</td>
              <td>${item.lotNumber ? `批次:${item.lotNumber}` : ''}${item.serialNumber ? ` 序列:${item.serialNumber}` : ''}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>

    <div class="section">
      <div class="section-title">配送說明 DELIVERY INSTRUCTIONS</div>
      <div style="min-height: 20px; padding: 2px;">
        ${packingOrder.deliveryInstructions || '請安全配送，如有問題請聯絡客服'}
      </div>
    </div>

    <div class="signature-section">
      <div class="signature-box">
        <div class="signature-label">配送員簽名 / 時間</div>
      </div>
      <div class="signature-box">
        <div class="signature-label">收件人簽名 / 時間</div>
      </div>
    </div>

    <div class="footer">
      <div>配送單生成時間: ${moment().format('YYYY-MM-DD HH:mm:ss')}</div>
      <div>訂單編號: ${packingOrder.orderNumber} | 配貨單: ${packingOrder.waybillNumber}</div>
      <div style="margin-top: 2px;">請妥善保管此配送單，如有問題請聯繫客服</div>
    </div>
  </div>
</body>
</html>`;
  }; */

  /* const generatePackingSlipPrintHTML = (packingOrder: PackingOrder): string => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>包裝單 - ${packingOrder.orderNumber}</title>
  <style>
    body { font-family: "Microsoft JhengHei", Arial, sans-serif; line-height: 1.4; margin: 0; padding: 20px; color: #333; font-size: 12px; }
    .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 12px; }
    .company-title { font-size: 18px; font-weight: bold; }
    .document-title { font-size: 16px; font-weight: bold; margin-top: 4px; }
    .meta { font-size: 12px; color: #666; }
    .section { margin-bottom: 12px; border: 1px solid #000; }
    .section-title { background: #f5f5f5; padding: 6px 8px; font-weight: bold; border-bottom: 1px solid #000; }
    .section-content { padding: 8px; }
    .grid { display: grid; grid-template-columns: 140px 1fr 140px 1fr; gap: 8px 12px; }
    .label { font-weight: bold; color: #000; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    th, td { border: 1px solid #000; padding: 6px 4px; font-size: 12px; }
    th { background: #f5f5f5; }
    @media print { body { margin: 0; padding: 12px; } .section { page-break-inside: avoid; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="company-title">Your Company Name</div>
    <div class="document-title">包裝單 / PACKING SLIP</div>
    <div class="meta">生成時間: ${new Date().toLocaleString()}</div>
  </div>

  <div class="section">
    <div class="section-title">基本信息</div>
    <div class="section-content">
      <div class="grid">
        <div class="label">訂單編號</div><div>${packingOrder.orderNumber}</div>
        <div class="label">配貨單號</div><div>${packingOrder.waybillNumber || '-'}</div>
        <div class="label">客戶名稱</div><div>${packingOrder.customerName}</div>
        <div class="label">收件人</div><div>${packingOrder.receiverName}</div>
        <div class="label">聯絡電話</div><div>${packingOrder.receiverContact || '-'}</div>
        <div class="label">配送地址</div><div>${packingOrder.deliveryAddress}</div>
        <div class="label">總件數</div><div>${packingOrder.totalItems}</div>
        <div class="label">總數量</div><div>${packingOrder.totalQuantity}</div>
        <div class="label">重量(kg)</div><div>${packingOrder.packageWeight}</div>
        <div class="label">尺寸</div><div>${packingOrder.packageDimensions}</div>
      </div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">商品明細</div>
    <div class="section-content">
      <table>
        <thead>
          <tr>
            <th style="width: 34%;">商品名稱</th>
            <th style="width: 18%;">SKU</th>
            <th style="width: 12%; text-align:center;">數量</th>
            <th>備註</th>
          </tr>
        </thead>
        <tbody>
          ${packingOrder.items.map(item => `
            <tr>
              <td>${item.productName}</td>
              <td>${item.productSku}</td>
              <td style="text-align:center;">${item.packedQuantity}</td>
              <td>${item.lotNumber ? `批次:${item.lotNumber}` : ''}${item.serialNumber ? ` 序列:${item.serialNumber}` : ''}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  </div>
</body>
</html>`;
  }; */

  const generateDeliveryNoteHTML = (packingOrder: PackingOrder) => {
    return (
      <div style={{ 
        border: '1px solid #e8e8e8', 
        borderRadius: '6px', 
        padding: '16px',
        background: '#fafafa'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
            {t('配送單預覽')}
                              </Title>
        </div>
        
        <Row gutter={16}>
          <Col span={12}>
            <Card title={t('配送信息')} size="small">
              <p><strong>{t('客戶:')}</strong> {packingOrder.customerName}</p>
              <p><strong>{t('收件人:')}</strong> {packingOrder.receiverName}</p>
              <p><strong>{t('聯絡電話:')}</strong> {packingOrder.receiverContact}</p>
              <p><strong>{t('配送日期:')}</strong> {moment().format('YYYY-MM-DD')}</p>
              <p><strong>{t('配送時間:')}</strong> 09:00-18:00</p>
            </Card>
          </Col>
          <Col span={12}>
            <Card title={t('包裹信息')} size="small">
              <p><strong>{t('配貨單號:')}</strong> {packingOrder.waybillNumber}</p>
              <p><strong>{t('追蹤號碼:')}</strong> {packingOrder.trackingNumber}</p>
              <p><strong>{t('重量:')}</strong> {packingOrder.packageWeight}kg</p>
              <p><strong>{t('尺寸:')}</strong> {packingOrder.packageDimensions}</p>
              <p><strong>{t('件數:')}</strong> {t('{{count}}件', { count: packingOrder.totalItems })}</p>
            </Card>
          </Col>
        </Row>
        
        <Card title={t('配送地址')} size="small" style={{ margin: '16px 0' }}>
          <p style={{ 
            background: 'white', 
            padding: '8px', 
            border: '1px solid #d9d9d9',
            borderRadius: '4px'
          }}>
            {packingOrder.deliveryAddress}
          </p>
        </Card>
        
        <Card title={t('商品明細')} size="small">
          <Table
            dataSource={packingOrder.items}
            columns={[
              { title: t('商品名稱'), dataIndex: 'productName', key: 'productName' },
              { title: 'SKU', dataIndex: 'productSku', key: 'productSku', width: 120 },
              { title: t('數量'), dataIndex: 'packedQuantity', key: 'packedQuantity', width: 80, align: 'center' },
              { 
                title: t('備註'), 
                key: 'remarks', 
                render: (_, record) =>
                  `${record.lotNumber ? `${t('批次:')}${record.lotNumber}` : ''}${record.serialNumber ? ` ${t('序列:')}${record.serialNumber}` : ''}`
              }
            ]}
            pagination={false}
            size="small"
            rowKey="id"
          />
        </Card>
      </div>
    );
  };

  // 生成配送單的邏輯已移除

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending': 'default',
      'packing': 'blue',
      'packed': 'purple',
      'ready_for_pickup': 'orange',
      'shipped': 'cyan',
      'delivered': 'green'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      'pending': t('待處理'),
      'packing': t('包裝中'),
      'packed': t('已包裝'),
      'ready_for_pickup': t('待攬收'),
      'shipped': t('已發貨'),
      'delivered': t('已收貨')
    };
    return texts[status] || status;
  };

  // 定義所有可用的包裝訂單列
  const allPackingColumns = {
    orderNumber: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePackingColumnSort('orderNumber')}
        >
          {t('訂單編號')}
                          {getPackingSortIcon('orderNumber')}
        </div>
      ),
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 140,
      sorter: true,
      render: (text: string) => <Text strong>{text}</Text>
    },
    waybillNumber: {
      title: t('配貨單號'),
      dataIndex: 'waybillNumber',
      key: 'waybillNumber',
      width: 140
    },
    customerName: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePackingColumnSort('customerName')}
        >
          {t('客戶')}
                          {getPackingSortIcon('customerName')}
        </div>
      ),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 150,
      sorter: true,
      render: (text: string) => <Text strong>{text}</Text>
    },
    receiverName: {
      title: t('收件人'),
      dataIndex: 'receiverName',
      key: 'receiverName',
      width: 120
    },
    status: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePackingColumnSort('status')}
        >
          {t('狀態')}
                          {getPackingSortIcon('status')}
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: true,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    packingZoneName: { title: t('打包區域'), dataIndex: 'packingZoneName', key: 'packingZoneName', width: 120 },
    pickupZoneName: { title: t('攬收區域'), dataIndex: 'pickupZoneName', key: 'pickupZoneName', width: 120 },
    itemStats: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePackingColumnSort('totalQuantity')}
        >
          {t('項目統計')}
                          {getPackingSortIcon('totalQuantity')}
        </div>
      ),
      key: 'itemStats',
      width: 120,
      sorter: true,
      render: (_: any, record: any) => <div style={{ fontSize: '12px' }}>{t('項目:')} {record.totalItems} {t('種')}</div>
    },
    totalQuantity: { title: t('總數量'), dataIndex: 'totalQuantity', key: 'totalQuantity', width: 100 },
    packageWeight: { title: t('總重量(kg)'), dataIndex: 'packageWeight', key: 'packageWeight', width: 110 },
    progress: {
      title: t('包裝進度'),
      key: 'progress',
      width: 120,
      render: (_: any, record: any) => {
        const totalQuantity = record.totalQuantity;
        const scannedQuantity = record.items.reduce((sum: number, item: any) => sum + (item.scannedQuantity || 0), 0);
        const packedQuantity = record.items.reduce((sum: number, item: any) => sum + (item.packedQuantity || 0), 0);
        const scannedPercent = totalQuantity > 0 ? Math.round((scannedQuantity / totalQuantity) * 100) : 0;
        const packedPercent = totalQuantity > 0 ? Math.round((packedQuantity / totalQuantity) * 100) : 0;
        
        return (
          <div>
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>
              {t('掃描:')} {scannedPercent}%
            </div>
            <Progress percent={scannedPercent} size="small" status="active" />
            <div style={{ fontSize: '12px', marginTop: '4px', marginBottom: '4px' }}>
              {t('打包:')} {packedPercent}%
            </div>
            <Progress percent={packedPercent} size="small" status="success" />
          </div>
        );
      }
    },
    deliveryCompany: { title: t('配送商'), dataIndex: 'deliveryCompany', key: 'deliveryCompany', width: 120 },
    trackingNumber: { title: t('追蹤號'), dataIndex: 'trackingNumber', key: 'trackingNumber', width: 140 },
    discrepancies: {
      title: t('差異'),
      key: 'discrepancies',
      width: 80,
      align: 'center' as 'center',
      render: (_: any, record: any) => {
        const discrepancyCount = record.items.filter((item: any) => item.hasDiscrepancy).length;
        return discrepancyCount > 0 ? (
          <Tooltip title={t('{{discrepancyCount}} 項差異', { discrepancyCount })}>
            <Badge count={discrepancyCount} status="error" />
          </Tooltip>
        ) : (
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
        );
      }
    },
    actions: {
      title: t('操作'),
      key: 'actions',
      width: 220,
      fixed: 'right' as 'right',
      render: (_: any, record: any) => (
        <Space wrap>
          <Button 
            size="small" 
            icon={<FileTextOutlined />}
            onClick={() => {
              setSelectedOrder(record);
              setPackingDetailVisible(true);
            }}
          >
            {t('詳情')}
                            </Button>
          
          {record.status === 'pending' && (
            <Button 
              size="small"
              type="primary"
              icon={<ScanOutlined />}
              onClick={() => {
                (async () => {
                  try {
                    await apiService.startPacking(record.id, {});
                    const detail: any = await apiService.getPackingOrderDetail(record.id);
                    const updated = { ...record, ...(detail || {}), status: 'packing' };
                    setPackingOrders(packingOrders.map(o => o.id === record.id ? updated : o));
                    setSelectedOrderForScan(updated as any);
                    setActiveTab('scanning');
                  } catch (e: any) {
                    message.error(e?.message || t('開始包裝失敗'));
                  }
                })();
              }}
            >
              {t('開始包裝')}
                                  </Button>
          )}
          
          {/*{record.status === 'packing' && (
            <Button 
              size="small"
              icon={<ScanOutlined />}
              onClick={() => {
                (async () => {
                  try {
                    const detail: any = await apiService.getPackingOrderDetail(record.id);
                    // 如果需要也可單獨拉 items：const items = await apiService.getPackingItems(record.id)
                    const updated = { ...record, ...(detail || {}) };
                    setPackingOrders(packingOrders.map(o => o.id === record.id ? updated : o));
                    setSelectedOrderForScan(updated as any);
                    setActiveTab('scanning');
                  } catch (e: any) {
                    message.error(e?.message || '加載包裝訂單詳情失敗');
                  }
                })();
              }}
            >
              繼續包裝
            </Button>
          )}*/}
          {/*{['packed', 'ready_for_pickup', 'shipped', 'delivered','packing'].includes(record.status) && (
            <Button 
              size="small"
              icon={<PrinterOutlined />}
              onClick={async () => {
                if(record.orderNumber.includes("H")) {// TODO HKTVmall【根據是否含有H 判斷是否是HKTVmall的 之後可以改（如果有其他規則）】
                  handleHktvMallDownloadPDF(record.orderNumber)
                }else {
                  handleDownloadPDF(record.orderNumber)
                }
              }}
            >
              列印
            </Button>
          )}*/}
          {/* 包裝單按鈕移至攬收管理 */}
          
          {record.status === 'ready_for_pickup' && (
            <Popconfirm
              title={t('確認已發貨？')}
              description={t('確認配送團隊已取走包裹？')}
              onConfirm={() => handleStatusChange(record.id, 'shipped')}
              okText={t('確認')}
              cancelText={t('取消')}
            >
              <Button 
                size="small"
                type="primary"
                icon={<CheckCircleOutlined />}
              >
                {t('確認發貨')}
                                        </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  };

  // 包裝訂單列設置菜單
  const packingColumnSettingsMenu = (
    <div style={{
      background: '#fff',
      borderRadius: '2px',
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>{t('列設置')}</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
      {Object.entries(allPackingColumns).map(([key, column]) => (
          <div
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handlePackingColumnVisibilityChange(key, !(packingColumnSettings[key]?.visible ?? true));
            }}
          >
          <Checkbox
            checked={packingColumnSettings[key]?.visible ?? true}
              onChange={(e) => {
                e.stopPropagation();
                handlePackingColumnVisibilityChange(key, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
          >
            {typeof column.title === 'string' ? column.title : 
             key === 'orderNumber' ? t('訂單編號') :
             key === 'waybillNumber' ? t('配貨單號') :
             key === 'customerName' ? t('客戶') :
             key === 'receiverName' ? t('收件人') :
             key === 'status' ? t('狀態') :
             key === 'packingZoneName' ? t('打包區域') :
             key === 'pickupZoneName' ? t('攬收區域') :
             key === 'itemStats' ? t('項目統計') :
             key === 'totalItems' ? t('項目數') :
             key === 'totalQuantity' ? t('總數量') :
             key === 'packageWeight' ? t('總重量(kg)') :
             key === 'progress' ? t('包裝進度') :
             key === 'deliveryCompany' ? t('配送商') :
             key === 'trackingNumber' ? t('追蹤號') :
             key === 'discrepancies' ? t('差異') :
             key === 'actions' ? t('操作') : key}
          </Checkbox>
          </div>
      ))}
      </div>
    </div>
  );

  // 根據列配置生成顯示的包裝訂單列
  const packingOrderColumns: ColumnsType<PackingOrder> = Object.entries(packingColumnSettings)
    .filter(([_, config]: [string, any]) => config.visible)
    .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
    .map(([key]) => {
      const column = allPackingColumns[key as keyof typeof allPackingColumns];
      if (!column) {
        console.warn(`Missing packing column definition for key: ${key}`);
      }
      return column;
    })
    .filter(Boolean); // 過濾掉undefined的列

  // 包裝訂單表支持拖拽列排序（禁用 actions）
  const { columns: draggablePackingColumns, order: packingOrder } = useDraggableColumns<PackingOrder>({
    columns: packingOrderColumns as any,
    disableDragKeys: ['actions'],
    storageKey: 'packing-orders-table',
  });

  useEffect(() => {
    if (!packingOrder || packingOrder.length === 0) return;
    setPackingColumnSettings((prev: any) => {
      const next: any = { ...prev };
      packingOrder.forEach((k: string, idx: number) => {
        if (next[k]) next[k] = { ...next[k], order: idx };
      });
      Object.keys(next).forEach((k) => {
        if (!packingOrder.includes(k)) {
          const o = next[k]?.order ?? 9999;
          next[k] = { ...next[k], order: Math.max(packingOrder.length, o) };
        }
      });
      try {
        localStorage.setItem('packing-columns-config', JSON.stringify(next));
        localStorage.setItem('packing-columns-version', '2025-01-v1');
      } catch {}
      return next;
    });
  }, [packingOrder]);

  // 處理包裝訂單數據過濾
  const filteredPackingOrders = packingOrders.filter(order => {
    const activeFilters = activeTab === 'pickup' ? pickupFilters : packingFilters;
    if (activeFilters.searchKeyword) {
      const keyword = activeFilters.searchKeyword.toLowerCase();
      const orderNumber = (order.orderNumber || '').toLowerCase();
      const customerName = (order.customerName || '').toLowerCase();
      const waybillNumber = (order.waybillNumber || '').toLowerCase();
      if (!orderNumber.includes(keyword) &&
          !customerName.includes(keyword) &&
          !waybillNumber.includes(keyword)) {
        return false;
      }
    }
    if (activeFilters.status && activeFilters.status.length > 0) {
      if (!activeFilters.status.includes(order.status)) return false;
    }
    return true;
  });

  // 處理包裝訂單數據排序
  const sortedAndFilteredPackingOrders = useMemo(() => {
    let result = [...filteredPackingOrders];
    
    if (packingSortField && packingSortOrder) {
      result.sort((a, b) => {
        let aValue: any = a[packingSortField as keyof PackingOrder];
        let bValue: any = b[packingSortField as keyof PackingOrder];
        
        // 處理數字字段排序
        if (['totalItems', 'totalQuantity', 'packageWeight', 'packageValue'].includes(packingSortField)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // 處理日期字段排序
        if (['createdAt', 'updatedAt', 'packedAt'].includes(packingSortField)) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        
        // 處理字符串字段排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return packingSortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return packingSortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [filteredPackingOrders, packingSortField, packingSortOrder]);

  // 攬收管理數據處理
  const pickupOrders = sortedAndFilteredPackingOrders.filter(order => 
    ['ready_for_pickup', 'shipped'].includes(order.status)
  );

  const sortedPickupOrders = useMemo(() => {
    let result = [...pickupOrders];
    
    if (pickupSortField && pickupSortOrder) {
      result.sort((a, b) => {
        let aValue: any = a[pickupSortField as keyof PackingOrder];
        let bValue: any = b[pickupSortField as keyof PackingOrder];
        
        // 處理數字字段排序
        if (['packageWeight', 'packageValue', 'totalItems', 'totalQuantity'].includes(pickupSortField)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // 處理日期字段排序
        if (['packedAt', 'createdAt', 'updatedAt'].includes(pickupSortField)) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        
        // 處理字符串字段排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return pickupSortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return pickupSortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [pickupOrders, pickupSortField, pickupSortOrder]);

  // 配送單管理數據處理
  const deliveryOrders = sortedAndFilteredPackingOrders.filter(order => 
    order.deliveryNoteNumber && ['ready_for_pickup', 'shipped', 'delivered'].includes(order.status)
  );

  const sortedDeliveryOrders = useMemo(() => {
    let result = [...deliveryOrders];
    
    if (deliverySortField && deliverySortOrder) {
      result.sort((a, b) => {
        let aValue: any = a[deliverySortField as keyof PackingOrder];
        let bValue: any = b[deliverySortField as keyof PackingOrder];
        
        // 處理數字字段排序
        if (['packageWeight', 'packageValue', 'totalItems', 'totalQuantity'].includes(deliverySortField)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // 處理日期字段排序
        if (['createdAt', 'updatedAt', 'packedAt'].includes(deliverySortField)) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        
        // 處理字符串字段排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return deliverySortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return deliverySortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [deliveryOrders, deliverySortField, deliverySortOrder]);

  // 定義所有可用的攬收管理列
  const allPickupColumns = {
    orderNumber: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePickupColumnSort('orderNumber')}
        >
          {t('訂單編號')}
                          {getPickupSortIcon('orderNumber')}
        </div>
      ),
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 140,
      sorter: true,
      render: (text: string, _record: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
        </div>
      )
    },
    waybillNumber: {
      title: t('配貨單號'),
      dataIndex: 'waybillNumber',
      key: 'waybillNumber',
      width: 140
    },
    customerName: { title: t('客戶名稱'), dataIndex: 'customerName', key: 'customerName', width: 160, sorter: true },
    receiverName: { title: t('收件人'), dataIndex: 'receiverName', key: 'receiverName', width: 120 },
    receiverContact: { title: t('聯絡電話'), dataIndex: 'receiverContact', key: 'receiverContact', width: 140 },
    pickupZoneName: { title: t('攬收區域'), dataIndex: 'pickupZoneName', key: 'pickupZoneName', width: 120 },
    packageType: { title: t('包裹類型'), dataIndex: 'packageType', key: 'packageType', width: 120 },
    packageWeight: { title: t('重量(kg)'), dataIndex: 'packageWeight', key: 'packageWeight', width: 110, sorter: true },
    packageDimensions: { title: t('尺寸'), dataIndex: 'packageDimensions', key: 'packageDimensions', width: 140 },
    packageValue: { title: t('價值'), dataIndex: 'packageValue', key: 'packageValue', width: 120, render: (v: number) => `$${Number(v || 0).toFixed(2)}` },
    deliveryCompany: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePickupColumnSort('deliveryCompany')}
        >
          {t('配送公司')}
                          {getPickupSortIcon('deliveryCompany')}
        </div>
      ),
      dataIndex: 'deliveryCompany',
      key: 'deliveryCompany',
      width: 120,
      sorter: true
    },
    trackingNumber: {
      title: t('追蹤號碼'),
      dataIndex: 'trackingNumber',
      key: 'trackingNumber',
      width: 140,
      render: (text: string) => (
        <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {text}
        </div>
      )
    },
    packedAt: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePickupColumnSort('packedAt')}
        >
          {t('打包時間')}
                          {getPickupSortIcon('packedAt')}
        </div>
      ),
      key: 'packedAt',
      width: 140,
      sorter: true,
      render: (_: any, record: any) => (
        record.packedAt ? moment(record.packedAt).format('YYYY-MM-DD HH:mm') : '-'
      )
    },
    status: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handlePickupColumnSort('status')}
        >
          {t('狀態')}
                          {getPickupSortIcon('status')}
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: true,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    actions: {
      title: t('操作'),
      key: 'actions',
      width: 220,
      fixed: 'right' as 'right',
      render: (_: any, record: any) => (
        <Space wrap>
          <Button 
            size="small" 
            icon={<FileTextOutlined />}
            onClick={() => {
              setSelectedOrder(record);
              setPackingDetailVisible(true);
            }}
          >
            {t('詳情')}
                            </Button>
          
          {record.status === 'ready_for_pickup' && (
            <Popconfirm
              title={t('確認已攬收？')}
              description={t('確認配送團隊已取走此包裹？')}
              onConfirm={() => handleStatusChange(record.id, 'shipped')}
              okText={t('確認')}
              cancelText={t('取消')}
            >
              <Button 
                size="small"
                type="primary"
                icon={<CheckCircleOutlined />}
              >
                {t('確認攬收')}
                                        </Button>
            </Popconfirm>
          )}
          
          {record.waybillNumber && (
            <Button 
              size="small"
              icon={<PrinterOutlined />}
              onClick={async () => {
                try {
                  await apiService.downloadLocalWaybillPDF(record.waybillNumber, record.pickOrderNumber);
                  message.success(t('包裝單已下載'));
                } catch (e) {
                  message.error(t('無法載入包裝單 PDF，請稍後重試'));
                }
              }}
            >
              {t('包裝單')}
                                  </Button>
          )}
        </Space>
      )
    }
  };

  // 攬收管理列設置菜單
  const pickupColumnSettingsMenu = (
    <div style={{
      background: '#fff',
      borderRadius: '2px',
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>{t('列設置')}</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
      {Object.entries(allPickupColumns).map(([key, column]) => (
          <div
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handlePickupColumnVisibilityChange(key, !(pickupColumnSettings[key]?.visible ?? true));
            }}
          >
          <Checkbox
            checked={pickupColumnSettings[key]?.visible ?? true}
              onChange={(e) => {
                e.stopPropagation();
                handlePickupColumnVisibilityChange(key, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
          >
            {typeof column.title === 'string' ? column.title : 
             key === 'orderNumber' ? t('訂單編號') :
             key === 'customerName' ? t('客戶名稱') :
             key === 'receiverName' ? t('收件人') :
             key === 'receiverContact' ? t('聯絡電話') :
             key === 'pickupZoneName' ? t('攬收區域') :
             key === 'packageType' ? t('包裹類型') :
             key === 'packageWeight' ? t('重量(kg)') :
             key === 'packageDimensions' ? t('尺寸') :
             key === 'packageValue' ? t('價值') :
             key === 'deliveryCompany' ? t('配送公司') :
             key === 'trackingNumber' ? t('追蹤號碼') :
             key === 'packedAt' ? t('打包時間') :
             key === 'status' ? t('狀態') :
             key === 'actions' ? t('操作') : key}
          </Checkbox>
          </div>
      ))}
      </div>
    </div>
  );

  // 根據列配置生成顯示的攬收管理列
  const pickupColumns = Object.entries(pickupColumnSettings)
    .filter(([_, config]: [string, any]) => config.visible)
    .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
    .map(([key]) => {
      const column = allPickupColumns[key as keyof typeof allPickupColumns];
      if (!column) {
        console.warn(`Missing pickup column definition for key: ${key}`);
      }
      return column;
    })
    .filter(Boolean);

  // 攬收管理表支持拖拽列排序（禁用 actions）
  const { columns: draggablePickupColumns, order: pickupOrder } = useDraggableColumns<PackingOrder>({
    columns: pickupColumns as any,
    disableDragKeys: ['actions'],
    storageKey: 'pickup-orders-table',
  });

  useEffect(() => {
    if (!pickupOrder || pickupOrder.length === 0) return;
    setPickupColumnSettings((prev: any) => {
      const next: any = { ...prev };
      pickupOrder.forEach((k: string, idx: number) => {
        if (next[k]) next[k] = { ...next[k], order: idx };
      });
      Object.keys(next).forEach((k) => {
        if (!pickupOrder.includes(k)) {
          const o = next[k]?.order ?? 9999;
          next[k] = { ...next[k], order: Math.max(pickupOrder.length, o) };
        }
      });
      try {
        localStorage.setItem('pickup-columns-config', JSON.stringify(next));
        localStorage.setItem('pickup-columns-version', '2025-02-v2');
      } catch {}
      return next;
    });
  }, [pickupOrder]);

  // 定義所有可用的配送單管理列
  const allDeliveryColumns = {
    deliveryNumber: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleDeliveryColumnSort('deliveryNoteNumber')}
        >
          {t('配送單編號')}
                          {getDeliverySortIcon('deliveryNoteNumber')}
        </div>
      ),
      key: 'deliveryNumber',
      width: 140,
      sorter: true,
      render: (_: any, record: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {record.deliveryNoteNumber || `DN-${record.id}`}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {t('訂單:')} {record.orderNumber}
          </div>
        </div>
      )
    },
    customer: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleDeliveryColumnSort('customerName')}
        >
          {t('客戶信息')}
                          {getDeliverySortIcon('customerName')}
        </div>
      ),
      key: 'customer',
      width: 200,
      sorter: true,
      render: (_: any, record: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.customerName}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {t('收件人:')} {record.receiverName}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {t('電話:')} {record.receiverContact}
          </div>
        </div>
      )
    },
    deliveryAddress: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleDeliveryColumnSort('deliveryAddress')}
        >
          {t('配送地址')}
                          {getDeliverySortIcon('deliveryAddress')}
        </div>
      ),
      dataIndex: 'deliveryAddress',
      key: 'deliveryAddress',
      width: 250,
      sorter: true,
      ellipsis: {
        showTitle: false,
      },
      render: (address: string) => (
        <Tooltip placement="topLeft" title={address}>
          {address}
        </Tooltip>
      )
    },
    delivery: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleDeliveryColumnSort('deliveryCompany')}
        >
          {t('配送公司')}
                          {getDeliverySortIcon('deliveryCompany')}
        </div>
      ),
      key: 'delivery',
      width: 120,
      sorter: true,
      render: (_: any, record: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {record.deliveryCompany}
          </div>
          {record.trackingNumber && (
            <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
              {record.trackingNumber}
            </div>
          )}
        </div>
      )
    },
    packageInfo: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleDeliveryColumnSort('packageWeight')}
        >
          {t('包裹信息')}
                          {getDeliverySortIcon('packageWeight')}
        </div>
      ),
      key: 'packageInfo',
      width: 120,
      sorter: true,
      render: (_: any, record: any) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            {t('重量:')} {record.packageWeight}kg
          </div>
          <div style={{ fontSize: '12px' }}>
            {t('件數:')} {t('{{count}}件', { count: record.totalItems })}
                            </div>
          <div style={{ fontSize: '12px' }}>
            {t('價值: $')}{typeof record.packageValue === 'number' ? record.packageValue.toFixed(2) : Number(record.packageValue || 0).toFixed(2)}
          </div>
        </div>
      )
    },
    status: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleDeliveryColumnSort('status')}
        >
          {t('配送狀態')}
                          {getDeliverySortIcon('status')}
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: true,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    createdAt: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleDeliveryColumnSort('createdAt')}
        >
          {t('創建時間')}
                          {getDeliverySortIcon('createdAt')}
        </div>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      sorter: true,
      render: (date: any) => moment(date).format('YYYY-MM-DD HH:mm')
    },
    actions: {
      title: t('操作'),
      key: 'actions',
      width: 200,
      render: (_: any, record: any) => (
        <Space wrap>
          <Button 
            size="small" 
            icon={<FileTextOutlined />}
            onClick={() => {
              setSelectedOrder(record);
              setDeliveryNoteVisible(true);
            }}
          >
            {t('查看配送單')}
                            </Button>
          
          <Button 
            size="small"
            icon={<PrinterOutlined />}
            onClick={() => handlePrintDeliveryNote(record)}
          >
            {t('重新列印')}
                            </Button>
          
          {record.status === 'shipped' && (
            <Popconfirm
              title={t('確認已送達？')}
              description={t('確認客戶已收到此包裹？')}
              onConfirm={() => handleStatusChange(record.id, 'delivered')}
              okText={t('確認')}
              cancelText={t('取消')}
            >
              <Button 
                size="small"
                type="primary"
                icon={<CheckCircleOutlined />}
              >
                {t('確認送達')}
                                        </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  };

  // 配送單管理列設置菜單
  const deliveryColumnSettingsMenu = (
    <div style={{
      background: '#fff',
      borderRadius: '2px',
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>{t('列設置')}</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
      {Object.entries(allDeliveryColumns).map(([key, column]) => (
          <div
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleDeliveryColumnVisibilityChange(key, !(deliveryColumnSettings[key]?.visible ?? true));
            }}
          >
          <Checkbox
            checked={deliveryColumnSettings[key]?.visible ?? true}
              onChange={(e) => {
                e.stopPropagation();
                handleDeliveryColumnVisibilityChange(key, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
          >
            {typeof column.title === 'string' ? column.title : 
             key === 'deliveryNumber' ? t('配送單編號') :
             key === 'customer' ? t('客戶信息') :
             key === 'deliveryAddress' ? t('配送地址') :
             key === 'delivery' ? t('配送公司') :
             key === 'packageInfo' ? t('包裹信息') :
             key === 'status' ? t('配送狀態') :
             key === 'createdAt' ? t('創建時間') :
             key === 'actions' ? t('操作') : key}
          </Checkbox>
          </div>
      ))}
      </div>
    </div>
  );

  // 根據列配置生成顯示的配送單管理列
  /* const deliveryColumns = Object.entries(deliveryColumnSettings)
    .filter(([_, config]: [string, any]) => config.visible)
    .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
    .map(([key]) => {
      const column = allDeliveryColumns[key as keyof typeof allDeliveryColumns];
      if (!column) {
        console.warn(`Missing delivery column definition for key: ${key}`);
      }
      return column;
    })
    .filter(Boolean); */

  return (
    <div style={{ padding: '24px' }}>
      {/* 標題 */}
      <div style={{ marginBottom: '8px' }}>
        <Title level={2} style={{ marginBottom: 0 }}>{t('包裝管理')}</Title>
        <Text type="secondary">{t('管理出庫訂單的包裝、攬收和配送流程')}</Text>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '12px' }}>
        <Tabs activeKey={activeTab} onChange={(k) => setActiveTab(k as any)}>
          {!user?.role.endsWith('SCANNING') && (
            <Tabs.TabPane tab={<span><InboxOutlined/> {t('包裝訂單')}</span>} key="packing"/>
          )}
          {user?.role !== 'PACK_SCANNING' && (
            <Tabs.TabPane tab={<span><TruckOutlined/> {t('攬收管理')}</span>} key="pickup"/>
          )}
          {user?.role !== 'PICKUP_SCANNING' &&
            <Tabs.TabPane tab={<span><ScanOutlined/> {t('掃描操作')}</span>} key="scanning"/>
          }
          {/* <Tabs.TabPane tab={<span><FileTextOutlined /> 配送單管理</span>} key="delivery" /> */}
        </Tabs>
      </div>

      {/* Tab Content */}
      {activeTab === 'packing' && (
        <>
          {/* Filters - Packing */}
          <Card style={{ marginBottom: '12px' }}>
            <Space size={16} wrap>
              <Input
                placeholder={t('訂單編號/客戶/配貨單')}
                prefix={<ScanOutlined />}
                value={packingSearchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setPackingSearchInput(value);
                  // If cleared (empty), immediately update filter without waiting for debounce
                  if (!value) {
                    setPackingFilters(prev => ({ ...prev, searchKeyword: '' }));
                  }
                }}
                allowClear
                style={{ width: 260 }}
              />
              <Select
                placeholder={t('狀態')}
                mode="multiple"
                value={packingFilters.status}
                onChange={(status) => setPackingFilters({ ...packingFilters, status })}
                allowClear
                style={{ width: 220 }}
              >
                <Option value="pending">{t('待處理')}</Option>
                <Option value="packing">{t('包裝中')}</Option>
                <Option value="packed">{t('已包裝')}</Option>
                <Option value="ready_for_pickup">{t('待攬收')}</Option>
                <Option value="shipped">{t('已發貨')}</Option>
                <Option value="delivered">{t('已收貨')}</Option>
              </Select>
              <Button icon={<ReloadOutlined />} onClick={loadPackingData} loading={loading}>{t('刷新')}</Button>
              <Space>
                <Input
                  placeholder={t('輸入訂單號 orderNo')}
                  allowClear
                  style={{ width: 220 }}
                  value={(packingFilters as any).printLabelOrderNo}
                  onChange={(e) => setPackingFilters({ ...packingFilters, printLabelOrderNo: e.target.value } as any)}
                />
                <Button
                    loading={splitLoading}
                    disabled={splitLoading}
                    onClick={async () => {
                      const orderNumber = String((packingFilters as any).printLabelOrderNo || '').trim();
                      if (!orderNumber) {
                        message.warning(t('請先輸入訂單號 orderNo'));
                        return;
                      }
                      try {
                        setSplitLoading(true);
                        const { PDFService } = await import('../../../../services/pdfService');
                        await PDFService.generatePDFZipFromAPIMap(
                          () => apiService.getHktvMallWaybillHtmlBatch({ orderNumber }),
                          {
                            zipFileName: `hktv-waybills-${orderNumber}.zip`,
                            filename: `hktv-waybills-${orderNumber}.zip`,
                            paperSize: 'a4',
                            orientation: 'portrait',
                            quality: 2,
                            useCORS: true,
                            backgroundColor: '#ffffff',
                            filenameTemplate: (waybillNo: string) => `hktv-waybill-${String(waybillNo).trim().replace(/[^\w.-]+/g, '_')}.pdf`
                          }
                        );
                      } catch (e: any) {
                        // Surface the underlying reason. Users report this as
                        // "Split Order not working" — but the backend split itself
                        // usually succeeds (splitOrder logs the split + link updates
                        // to batch_picking_order_links); the failure is normally in
                        // the client-side html2canvas / jsPDF ZIP step. Without the
                        // underlying reason in the toast, we can't tell which layer
                        // to investigate. Full error still logs to DevTools Console.
                        console.error('Split Order failed:', e);
                        const reason =
                          (e && (e.message || (typeof e.toString === 'function' ? e.toString() : ''))) ||
                          t('未知錯誤（請開啟 DevTools Console 查看詳情）');
                        message.error(t('前端生成 HKTV Waybill ZIP 失敗：{{reason}}', { reason }), 8);
                      } finally {
                        setSplitLoading(false);
                      }
                    }}
                >
                  {t('Split Order')}
                                                  </Button>
                <Button icon={<FileExcelOutlined />} onClick={() => handleExportExcel('packing')}>{t('導出')}</Button>
              </Space>
            </Space>
          </Card>

          {/* List - Packing */}
          <Card
            title={
              <Space>
                <InboxOutlined />
                <span>{t('包裝訂單列表')}</span>
                <Badge count={filteredPackingOrders.length} />
              </Space>
            }
            extra={
              <Space>
                <Dropdown
                  overlay={packingColumnSettingsMenu}
                  trigger={['click']}
                  open={packingColumnSettingsVisible}
                  onOpenChange={setPackingColumnSettingsVisible}
                >
                  <Button icon={<SettingOutlined />}>
                    {t('列設置')}
                                              </Button>
                </Dropdown>
              </Space>
            }
          >
            <Table
              columns={draggablePackingColumns as any}
              dataSource={sortedAndFilteredPackingOrders}
              rowKey="id"
              loading={loading}
              pagination={{
                current: packingPage,
                pageSize: packingPageSize,
                total: packingTotal,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: [10, 20, 50, 100, 500],
                showTotal: (totalCount, range) => t('顯示 {{from}}-{{to}} 項，共 {{total}} 項記錄', { from: range[0], to: range[1], total: totalCount }),
                onChange: (p, ps) => {
                  const newSize = ps || packingPageSize;
                  const newPage = p || 1;
                  if (newSize !== packingPageSize) {
                    setPackingPageSize(newSize);
                    setPackingPage(1);
                  } else {
                    setPackingPage(newPage);
                  }
                }
              }}
              scroll={{ x: 1600 }}
            />
          </Card>
        </>
      )}

      {activeTab === 'pickup' && (
        <>
          {/* KPI 概覽 - 攬收管理 */}
          <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
            <Row gutter={16}>
              <Col flex={1}>
                <Card bordered hoverable>
                  <Statistic 
                    title={t('已包裝 (待掃描攬收)')} 
                    value={kpi.packed}
                    prefix={<InboxOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col flex={1}>
                <Card bordered hoverable>
                  <Statistic 
                    title={t('待攬收 (已掃描)')} 
                    value={kpi.readyForPickup}
                    prefix={<TruckOutlined />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
              <Col flex="auto">
                <Card bordered hoverable>
                  <Statistic 
                    title={t('總待處理')} 
                    value={kpi.packed + kpi.readyForPickup}
                    prefix={<InboxOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
            </Row>
          </Card>
          
          {/* 攬收掃描界面 */}
          <PickupScanInterface
            onUpdateOrder={() => {
              refreshKpi();
            }}
          />
        </>
      )}

      {activeTab === 'scanning' && (
        <>
          {/* KPI 概覽 - 始终显示在扫描页面顶部 */}
          <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
            <Row gutter={16}>
              <Col flex={1}>
                <Card bordered hoverable>
                  <Statistic title={t('核對中 (Pick 完成/ 未包裝)')} value={kpi.verifying} prefix={<InboxOutlined />} />
                </Card>
              </Col>
              <Col flex={1}>
                <Card bordered hoverable>
                  <Statistic title={t('已包裝 (Pack完成)')} value={kpi.packed} />
                </Card>
              </Col>
            </Row>
          </Card>
          
          <PackingScanInterface
            packingOrder={selectedOrderForScan}
            onUpdatePackingOrder={(updated) => {
              setPackingOrders(packingOrders.map(order => 
                order.id === updated.id ? updated : order
              ));
              setSelectedOrderForScan(updated);
              // 在扫描订单完成提交时刷新 KPI
              refreshKpi();
            }}
            onClose={() => {
              setActiveTab('packing');
              setSelectedOrderForScan(null);
            }}
          />
        </>
      )}

      {activeTab === 'pickup-scanning' && (
        <>
          {/* KPI 概覽 - 攬收掃描頁面 */}
          <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
            <Row gutter={16}>
              <Col flex={1}>
                <Card bordered hoverable>
                  <Statistic 
                    title={t('已包裝 (待掃描攬收)')} 
                    value={kpi.packed} 
                    prefix={<InboxOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col flex={1}>
                <Card bordered hoverable>
                  <Statistic 
                    title={t('待攬收 (已掃描)')} 
                    value={kpi.readyForPickup}
                    prefix={<TruckOutlined />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
              <Col flex="auto">
                <Card bordered hoverable style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Space>
                    <Button 
                      icon={<ReloadOutlined />}
                      onClick={loadPickupData}
                      loading={loading}
                    >
                      {t('刷新數據')}
                                                              </Button>
                    <Button 
                      onClick={() => setActiveTab('pickup')}
                    >
                      {t('返回列表')}
                                                              </Button>
                  </Space>
                </Card>
              </Col>
            </Row>
          </Card>
          
          <PickupScanInterface
            onUpdateOrder={() => {
              loadPickupKpi();
            }}
          />
        </>
      )}

      {/*{activeTab === 'delivery' && (
        <Card
          title={
            <Space>
              <FileTextOutlined />
              <span>配送單管理</span>
              <Badge count={deliveryOrders.length} />
            </Space>
          }
          extra={
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={loadData}
                loading={loading}
              >
                刷新
              </Button>
              <Dropdown
                overlay={deliveryColumnSettingsMenu as any}
                trigger={['click']}
                open={deliveryColumnSettingsVisible}
                onOpenChange={setDeliveryColumnSettingsVisible}
              >
                <Button icon={<SettingOutlined />}>
                  列設置
                </Button>
              </Dropdown>
            </Space>
          }
        >
          <Alert
            message="配送單管理功能"
            description="此頁面管理所有已生成的配送單，可以查看配送狀態和重新列印配送單。"
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />
          
          <Table
            columns={deliveryColumns as any}
            dataSource={sortedDeliveryOrders}
            rowKey="id"
            loading={loading}
            pagination={{
              total: sortedDeliveryOrders.length,
              defaultPageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: [10, 20, 50, 100, 500],
              showTotal: (total) => `共 ${total} 條記錄`
            }}
            scroll={{ x: 1400 }}
          />
        </Card>
      )}*/}

      {/* Delivery Note Modal */}
      <Modal
        title={
          selectedOrder && (
            <Space>
              <FileTextOutlined />
              <span>{t('配送單 -')} {selectedOrder.deliveryNoteNumber}</span>
            </Space>
          )
        }
        open={deliveryNoteVisible}
        onCancel={() => setDeliveryNoteVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDeliveryNoteVisible(false)}>
            {t('關閉')}
                          </Button>,
          <Button 
            key="print" 
            type="primary" 
            icon={<PrinterOutlined />}
            onClick={() => selectedOrder && handlePrintDeliveryNote(selectedOrder)}
          >
            {t('列印配送單')}
                        </Button>
        ]}
        width={800}
      >
        {selectedOrder && (
          <div>
            {generateDeliveryNoteHTML(selectedOrder)}
          </div>
        )}
      </Modal>

      {/* Package Detail Modal */}
      <Modal
        title={
          selectedOrder && (
            <Space>
              <FileTextOutlined />
              <span>{t('包裝訂單詳情 -')} {selectedOrder.orderNumber}</span>
            </Space>
          )
        }
        open={packingDetailVisible}
        onCancel={() => setPackingDetailVisible(false)}
        footer={null}
        width={1000}
      >
        {selectedOrder && (
          <div>
            {/* Basic Info */}
            <Descriptions
              title={t('基本信息')}
              bordered
              column={2}
              style={{ marginBottom: '24px' }}
            >
              <Descriptions.Item label={t('訂單編號')}>
                {selectedOrder.orderNumber}
              </Descriptions.Item>
              <Descriptions.Item label={t('配貨單編號')}>
                {selectedOrder.waybillNumber}
              </Descriptions.Item>
              <Descriptions.Item label={t('狀態')}>
                <Tag color={getStatusColor(selectedOrder.status)}>
                  {getStatusText(selectedOrder.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('包裝區域')}>
                {selectedOrder.packingZoneName}
              </Descriptions.Item>
              <Descriptions.Item label={t('攬收區域')}>
                {selectedOrder.pickupZoneName}
              </Descriptions.Item>
              <Descriptions.Item label={t('配送公司')}>
                {selectedOrder.deliveryCompany}
              </Descriptions.Item>
            </Descriptions>

            {/* Customer Info */}
            <Row gutter={16} style={{ marginBottom: '24px' }}>
              <Col span={12}>
                <Card title={t('客戶信息')} size="small">
                  <div style={{ marginBottom: '8px' }}>
                    <UserOutlined /> <strong>{selectedOrder.customerName}</strong>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <PhoneOutlined /> {selectedOrder.customerContact}
                  </div>
                  <div>
                    <EnvironmentOutlined /> {selectedOrder.deliveryAddress}
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card title={t('收件人信息')} size="small">
                  <div style={{ marginBottom: '8px' }}>
                    <UserOutlined /> <strong>{selectedOrder.receiverName}</strong>
                  </div>
                  <div>
                    <PhoneOutlined /> {selectedOrder.receiverContact}
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Package Info */}
            <Card title={t('包裹信息')} size="small" style={{ marginBottom: '24px' }}>
              <Row gutter={16}>
                <Col span={6}>
                  <div><strong>{t('包裝類型:')}</strong> {selectedOrder.packageType}</div>
                </Col>
                <Col span={6}>
                  <div><strong>{t('重量:')}</strong> {selectedOrder.packageWeight}kg</div>
                </Col>
                <Col span={6}>
                  <div><strong>{t('尺寸:')}</strong> {selectedOrder.packageDimensions}</div>
                </Col>
                <Col span={6}>
                  <div><strong>{t('價值:')}</strong> ${typeof selectedOrder.packageValue === 'number' ? selectedOrder.packageValue.toFixed(2) : Number(selectedOrder.packageValue || 0).toFixed(2)}</div>
                </Col>
              </Row>
            </Card>

            {/* Items List */}
            <div>
              <Title level={5}>{t('包裝項目')}</Title>
              <Table
                dataSource={selectedOrder.items}
                columns={[
                  {
                    title: 'SKU',
                    dataIndex: 'productSku',
                    key: 'productSku',
                    width: 120
                  },
                  {
                    title: t('產品名稱'),
                    dataIndex: 'productName',
                    key: 'productName'
                  },
                  {
                    title: t('預期數量'),
                    dataIndex: 'expectedQuantity',
                    key: 'expectedQuantity',
                    width: 80,
                    align: 'center'
                  },
                  {
                    title: t('掃描數量'),
                    dataIndex: 'scannedQuantity',
                    key: 'scannedQuantity',
                    width: 80,
                    align: 'center'
                  },
                  {
                    title: t('包裝數量'),
                    dataIndex: 'packedQuantity',
                    key: 'packedQuantity',
                    width: 80,
                    align: 'center'
                  },
                  {
                    title: t('狀態'),
                    dataIndex: 'status',
                    key: 'status',
                    width: 100,
                    render: (status, record) => {
                      if (record.hasDiscrepancy) {
                        return <Tag color="red">{t('差異')}</Tag>;
                      }
                      const statusColors: Record<string, string> = {
                        'pending': 'default',
                        'scanned': 'blue',
                        'packed': 'green',
                        'verified': 'success'
                      };
                      const statusTexts: Record<string, string> = {
                        'pending': t('待處理'),
                        'scanned': t('已掃描'),
                        'packed': t('已包裝'),
                        'verified': t('已核對')
                      };
                      return <Tag color={statusColors[status]}>{statusTexts[status]}</Tag>;
                    }
                  },
                  {
                    title: t('批次/序列號'),
                    key: 'lotSerial',
                    render: (_, record) => (
                      <div>
                        {record.lotNumber && (
                          <div style={{ fontSize: '12px' }}>
                            {t('批次:')} {record.lotNumber}
                          </div>
                        )}
                        {record.serialNumber && (
                          <div style={{ fontSize: '12px' }}>
                            {t('序列:')} {record.serialNumber}
                          </div>
                        )}
                      </div>
                    )
                  }
                ]}
                pagination={false}
                size="small"
                rowKey="id"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PackingManagement;
